"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";

// Bundle-size discipline (2026-07-16 trim pass): the interactive island
// (BirthDetailsForm + PlaceAutocomplete + result stack) lives in
// ./AscendantCalculatorClient and loads via next/dynamic, sharing ONE copy of
// the form stack with the other deferred calculator islands. The header/H1 +
// SEO prose render immediately server-side (rules 43/61); the shell reserves
// the swap height (rule 58).
const AscendantToolIsland = dynamic(() => import("./AscendantCalculatorClient"), {
  ssr: false,
  loading: () => <BirthToolFormShell />,
});

export default function DeferredAscendantCalculatorClient({
  crossLinks,
  language = "en",
}: {
  crossLinks: ReactNode;
  language?: "en" | "hi";
}) {
  const { ready, activate } = useDeferredToolActivation();

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <AscendantHeader />

        {ready ? (
          <AscendantToolIsland language={language} />
        ) : (
          <BirthToolFormShell
            language={language}
            submitLabel={language === "hi" ? "मेरा लग्न बताएं" : "Find My Ascendant"}
            onActivate={activate}
          />
        )}

        <AscendantSeoContent />

        {crossLinks}
      </div>
    </div>
  );
}

function AscendantHeader() {
  return (
    <div className="text-center mb-10">
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3"
        style={{ color: "var(--ish-accent)" }}
      >
        Lagna Ganana
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        Free Lagna (Ascendant) Calculator
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto text-ish-text-muted">
        Enter your birth date, time, and place to find your Lagna (rising sign). Get your Ascendant
        Rashi, Lagna lord, and personality traits — 100% free.
      </p>

      <div
        className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium"
        style={{ color: "var(--ish-text-faint)" }}
      >
        <TrustSignal icon="shield" label="Accurate Vedic calculations" />
        <TrustSignal icon="clock" label="Instant Results" />
        <TrustSignal icon="user" label="100% Free" />
      </div>

      <div
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px]"
        style={{
          backgroundColor: "var(--ish-accent-light)",
          color: "var(--ish-accent)",
          border: "1px solid var(--ish-accent-border)",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
        Birth time is crucial for accurate ascendant calculation
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}

function AscendantSeoContent() {
  return (
    <div className="mt-12 card-sacred p-6 sm:p-8">
      <h2
        className="text-[20px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        What is the Ascendant (Lagna)?
      </h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          The <strong style={{ color: "var(--ish-dark)" }}>Ascendant</strong> (Lagna in Sanskrit) is
          the zodiac sign that was rising on the eastern horizon at the exact moment and location of
          your birth. It is the most time-sensitive point in your birth chart — it changes
          approximately every 2 hours, which is why knowing your precise birth time is essential.
        </p>
        <p>
          In Vedic astrology, the Ascendant is considered even more important than the Sun or Moon
          sign. It determines the layout of all 12 houses in your chart and influences your physical
          appearance, health, personality, and how the world perceives you. The lord of the
          Ascendant sign (Lagnesh) becomes the most important planet in your horoscope.
        </p>
        <p>
          While your Moon sign (Rashi) reflects your emotional inner world, your Ascendant reflects
          your outward personality, physical constitution, and the lens through which you experience
          life. A complete Vedic reading considers both alongside the Sun sign for a holistic
          understanding.
        </p>
      </div>
    </div>
  );
}
